import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const INTERFACE_STORAGE_KEY = 'at_interface_version';
export const INTERFACE_VERSIONS = [
  { value: 'alpha', label: 'Alpha' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'simple', label: 'Simple' },
];
const InterfaceContext = createContext(null);

const isInterfaceVersion = (value) => INTERFACE_VERSIONS.some((version) => version.value === value);

function readInterface() {
  try {
    const stored = window.localStorage.getItem(INTERFACE_STORAGE_KEY);
    return isInterfaceVersion(stored) ? stored : 'alpha';
  } catch {
    return 'alpha';
  }
}

export function InterfaceProvider({ children }) {
  const [version, setVersion] = useState(readInterface);

  const selectVersion = useCallback((nextVersion) => {
    if (!isInterfaceVersion(nextVersion)) return;
    setVersion(nextVersion);
    try {
      window.localStorage.setItem(INTERFACE_STORAGE_KEY, nextVersion);
    } catch {
      // Switching still works when the browser blocks persistent storage.
    }
  }, []);

  useEffect(() => {
    // Bootstrap and MUI can render menus/dialogs outside the application root.
    const root = document.documentElement;
    const previous = root.getAttribute('data-interface');
    root.setAttribute('data-interface', version);
    return () => {
      if (previous === null) root.removeAttribute('data-interface');
      else root.setAttribute('data-interface', previous);
    };
  }, [version]);

  const value = useMemo(() => ({ version, selectVersion }), [version, selectVersion]);
  return <InterfaceContext.Provider value={value}>{children}</InterfaceContext.Provider>;
}

export function useInterface() {
  const context = useContext(InterfaceContext);
  if (!context) throw new Error('useInterface must be used inside InterfaceProvider');
  return context;
}
